<template>
  <l-modal
    :title="t('bill-discount.title-plural')"
    :button-text="t('bill-discount.save')"
    :button-enabled="!wrongAmount"
    @action="save"
    @close="close"
  >
    <l-option-selector v-model="selectedOption" :options="discountTypes" />

    <div v-if="selectedOption === 'promotion'" class="mt-4">
      <div v-if="promotionsWithoutPoints.length > 0">
        <div class="flex heading-3">
          {{ t('bill-discount.no-points-discounts') }}
        </div>
        <div class="grid grid-cols-3 gap-3 my-6">
          <promotion-item
            v-for="promotion in promotionsWithoutPoints"
            :key="promotion.id"
            :promotion="promotion"
            :selected="editingDiscount.promotionId === promotion.id"
            @click="promotionSelected(promotion)"
          />
        </div>
      </div>
      <div v-if="promotionsWithPoints.length > 0">
        <div class="flex flex-col">
          <div class="heading-3">
            {{ t('bill-discount.points-discounts') }}
          </div>
          <div class="font-body">
            {{ t('bill-discount.available-points') }}:
            {{ customerPoints }}
          </div>
        </div>
        <div class="flex flex-wrap gap-3 my-6">
          <promotion-item
            v-for="promotion in promotionsWithPoints"
            :key="promotion.id"
            :promotion="promotion"
            :selected="editingDiscount.promotionId === promotion.id"
            @click="promotionSelected(promotion)"
          />
        </div>
      </div>
    </div>

    <div v-else class="mt-4">
      <l-field :label="t('bill-discount.discount-to-apply')">
        <l-number-input
          v-if="editingDiscount.type === 'currency'"
          v-model="editingDiscount.amount"
          :min="0"
          :icon="currencyIcon"
          format="currency"
        />
        <l-number-input
          v-else
          v-model="editingDiscount.amount"
          :min="0"
          :max="100"
          icon="percentage"
          format="decimal"
        />
      </l-field>
    </div>
  </l-modal>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { PromotionFilter } from '@last/core'
import { LField, LModal, LNumberInput, LOptionSelector } from '@last/core-ui/paprika'
import type { Option } from '@last/core-ui/paprika/components/LOptionSelector.vue'

import { getCustomerPromotions } from '@/api/customers'
import { useTabs } from '@/composables/useTabs'
import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config'
import { usePromotionsStore } from '@/store/promotions'
import type { Discount, Promotion } from '@/types'

import PromotionItem from './PromotionItem.vue'

interface Props {
  tabId: string
  originalTotal: number
}

const props = defineProps<Props>()

const discount = defineModel<Discount | null>('discount')

const emit = defineEmits<{
  close: []
  cancel: []
}>()

const { t } = useI18n()

const authStore = useAuthStore()
const promotionsStore = usePromotionsStore()
const configStore = useConfigStore()

const { locationId } = storeToRefs(authStore)
const {
  promotionsWithPoints: getPromotionsWithPoints,
  promotionsWithoutPoints: getPromotionsWithoutPoints
} = storeToRefs(promotionsStore)
const { currencyIcon } = storeToRefs(configStore)

const { tab, customerId, total } = useTabs(() => props.tabId)

const customerPoints = ref(0)
const editingDiscount = ref<Discount>({
  type: 'percentage',
  amount: 0,
  global: true
})
const customerRestrictedPromotions = ref<Promotion[]>([])
const customerUsedPromotions = ref<string[]>([])
const selectedOption = ref<'percentage' | 'currency' | 'promotion'>('percentage')

const wrongAmount = computed(() => {
  if (editingDiscount.value.type === 'currency') {
    if (editingDiscount.value.amount > props.originalTotal) {
      return true
    }
  } else if (editingDiscount.value.type === 'percentage') {
    const amount = parseInt(String(editingDiscount.value.amount))
    if (amount < 0 || amount > 100 || isNaN(amount)) return true
  }
  return false
})

const promotionsWithPoints = computed(() => {
  return getPromotionsWithPoints.value
    .concat(customerRestrictedPromotions.value)
    .filter(promotion => {
      return (
        checkIfPromotionCanBeShown(promotion) &&
        promotion.pointsExpense &&
        promotion.pointsExpense <= customerPoints.value
      )
    }) as (Promotion & { discountType: 'currency' | 'percentage' })[]
})

const promotionsWithoutPoints = computed(() => {
  return getPromotionsWithoutPoints.value
    .concat(customerRestrictedPromotions.value)
    .filter(promotion => {
      return checkIfPromotionCanBeShown(promotion)
    }) as (Promotion & { discountType: 'currency' | 'percentage' })[]
})

const discountTypes = computed(() => {
  const types: Option[] = [
    {
      icon: 'cash',
      value: 'currency',
      label: t('product-discount.cash')
    },
    {
      icon: 'percentage',
      value: 'percentage',
      label: t('product-discount.percentage')
    }
  ]
  if (promotionsWithPoints.value.length > 0 || promotionsWithoutPoints.value.length > 0) {
    types.push({
      icon: 'discount',
      value: 'promotion',
      label: t('bill-discount.promotion')
    })
  }
  return types
})

function close() {
  emit('close')
}

function promotionSelected(promotion: Promotion & { discountType: 'currency' | 'percentage' }) {
  if (editingDiscount.value.promotionId === promotion.id) {
    editingDiscount.value = {
      type: 'percentage',
      amount: 0,
      global: true
    }
  } else {
    editingDiscount.value = {
      type: promotion.discountType,
      amount: promotion.discountAmount!,
      promotionId: promotion.id,
      pointsExpense: promotion.pointsExpense!,
      promotionName: promotion.name,
      freeDelivery: promotion.freeDelivery,
      global: true
    }
  }
}

function save() {
  if (!editingDiscount.value.amount) {
    editingDiscount.value.amount = 0
  }
  discount.value = {
    ...editingDiscount.value
  }
  emit('close')
}

function checkIfPromotionCanBeShown(promotion: Promotion) {
  const isNotProductOrCategory = !promotion.products && !promotion.categories

  let isCorrectAmount = true
  if (promotion.discountType === 'currency') {
    isCorrectAmount = promotion.discountAmount! <= props.originalTotal
  }
  const isCorrectType = ['currency', 'percentage'].includes(promotion.discountType)

  const today = format(new Date(), 'eeee', { locale: enUS }).toLowerCase()

  return (
    isNotProductOrCategory &&
    isCorrectType &&
    isCorrectAmount &&
    new PromotionFilter(
      total.value!,
      locationId.value,
      today,
      tab.value!.pickupType,
      customerId.value,
      customerUsedPromotions.value
    ).isPromotionValid(promotion)
  )
}

onMounted(async () => {
  if (discount.value) {
    if (discount.value.promotionId) {
      selectedOption.value = 'promotion'
    } else {
      selectedOption.value = discount.value.type
    }
    editingDiscount.value = discount.value
  }
  if (customerId.value) {
    const response = await getCustomerPromotions(customerId.value)
    customerPoints.value = response.points
    customerRestrictedPromotions.value = response.customerPromotions
    customerUsedPromotions.value = response.customerUsedPromotions
  }
})

watch(
  selectedOption,
  () => {
    if (selectedOption.value !== 'promotion') {
      editingDiscount.value = {
        type: selectedOption.value,
        amount: 0,
        global: true
      }
    }
  },
  { immediate: true }
)
</script>
